// Seed defaults for the public /pricing page and the admin editor.
//
// Strategy: derive a pricing table from the actual room registry inside
// `hotelDetails.js`. Each property's category sets the base nightly rate
// (room-only) and we apply a small additive delta for B&B, half-board
// and full-board. Per-room multipliers nudge rates up for larger rooms,
// suites and family configurations so the seeded list never reads as
// "all flat at €120".

import { HOTELS } from "@/lib/data";
import { getHotelDetails } from "@/lib/hotelDetails";

// Base room-only nightly rate in EUR by hotel category.
const BASE_BY_CATEGORY = {
  hotel: 120,
  bivouac: 180,
  refuge: 110,
};

// Multiplier applied based on a fuzzy match against the room's English
// name. Captures suites/family/superior premiums without needing every
// room to carry an explicit tier.
const ROOM_MULTIPLIERS = [
  { test: /suite (vip|royal|signature)/i, mult: 2.3 },
  { test: /(xaluca|presidential|royal) suite/i, mult: 2.1 },
  { test: /junior suite/i, mult: 1.55 },
  { test: /\bsuite\b/i, mult: 1.75 },
  { test: /family|familiale|familiar/i, mult: 1.35 },
  { test: /superior|sup[ée]rieure?/i, mult: 1.2 },
  { test: /tent (royal|signature)|tente royale/i, mult: 1.8 },
  { test: /tent|tente|haima/i, mult: 1.0 },
  { test: /twin/i, mult: 1.0 },
  { test: /double/i, mult: 1.0 },
  { test: /standard|estandar/i, mult: 1.0 },
];

const pickMultiplier = (roomNameEn = "") => {
  for (const { test, mult } of ROOM_MULTIPLIERS) {
    if (test.test(roomNameEn)) return mult;
  }
  return 1.0;
};

// Board-basis additive deltas (EUR / night / person, applied as a per-room
// add given a single rate field — kept simple for the public table).
const BOARD_DELTAS = {
  room_only: 0,
  bb: 18,
  hb: 38,
  fb: 60,
};

const round5 = (n) => Math.round(n / 5) * 5;

/**
 * Build a default pricing table for a property from its room registry.
 * Returns an array of pricing rows in the same shape used by the API.
 */
export const buildDefaultPricingForHotel = (hotelOrSlug) => {
  const hotel =
    typeof hotelOrSlug === "string"
      ? HOTELS.find((h) => h.slug === hotelOrSlug)
      : hotelOrSlug;
  if (!hotel) return [];

  const details = getHotelDetails(hotel.slug, hotel.category);
  const rooms = (details && details.rooms) || [];
  if (!rooms.length) return [];

  const base = BASE_BY_CATEGORY[hotel.category] || 120;

  return rooms.map((r, idx) => {
    const en = (r.name && r.name.en) || "";
    const mult = pickMultiplier(en);
    const roomOnly = round5(base * mult);
    return {
      id: `default-${hotel.slug}-${r.id || idx}`,
      room_id: r.id || `room-${idx}`,
      room_name: r.name || { en, fr: en, es: en },
      capacity: r.capacity || { en: "", fr: "", es: "" },
      characteristics: {
        en: (r.desc && r.desc.en) ? r.desc.en.split(".")[0] : "",
        fr: (r.desc && r.desc.fr) ? r.desc.fr.split(".")[0] : "",
        es: (r.desc && r.desc.es) ? r.desc.es.split(".")[0] : "",
      },
      prices: {
        room_only: roomOnly,
        bb: roomOnly + BOARD_DELTAS.bb,
        hb: roomOnly + BOARD_DELTAS.hb,
        fb: roomOnly + BOARD_DELTAS.fb,
      },
    };
  });
};

// Static FX rates for the public currency switcher. Conservative
// snapshots; refreshed manually by the brand team as needed. EUR is the
// base — every backend row stores prices in EUR.
export const FX_RATES = {
  EUR: { code: "EUR", symbol: "€", rate: 1.0, after: false },
  USD: { code: "USD", symbol: "$", rate: 1.07, after: false },
  MAD: { code: "MAD", symbol: "MAD", rate: 10.7, after: true },
};

/**
 * Format an EUR amount in the chosen currency, rounded to a clean
 * thousands-friendly increment. Returns "—" for null/undefined.
 */
export const formatPrice = (eurAmount, currency = "EUR") => {
  if (eurAmount === null || eurAmount === undefined || eurAmount === "") return "—";
  const conf = FX_RATES[currency] || FX_RATES.EUR;
  const converted = Number(eurAmount) * conf.rate;
  const rounded =
    currency === "MAD" ? Math.round(converted / 10) * 10 : Math.round(converted);
  return conf.after
    ? `${rounded.toLocaleString("en-US")} ${conf.symbol}`
    : `${conf.symbol}${rounded.toLocaleString("en-US")}`;
};

export const BOARD_BASIS_LABELS = {
  room_only: { en: "Room only", fr: "Chambre seule", es: "Sólo habitación" },
  bb: { en: "B&B", fr: "Petit-déjeuner", es: "Desayuno" },
  hb: { en: "Half-board", fr: "Demi-pension", es: "Media pensión" },
  fb: { en: "Full-board", fr: "Pension complète", es: "Pensión completa" },
};

// Full user-facing sentences used on public price blocks. These match the
// brand's canonical wording — always "included" for plans that carry
// meals so the guest sees a single, consistent value proposition.
export const BOARD_BASIS_INCLUDED_LABELS = {
  room_only:  { en: "Room only",             fr: "Hébergement seul",       es: "Solo alojamiento" },
  bb:         { en: "Breakfast included",    fr: "Petit-déjeuner inclus",  es: "Desayuno incluido" },
  hb:         { en: "Half-board included",   fr: "Demi-pension incluse",   es: "Media pensión incluida" },
  fb:         { en: "Full-board included",   fr: "Pension complète incluse", es: "Pensión completa incluida" },
  // Aliases for legacy consumers that use the alternative keys.
  breakfast:  { en: "Breakfast included",    fr: "Petit-déjeuner inclus",  es: "Desayuno incluido" },
  half_board: { en: "Half-board included",   fr: "Demi-pension incluse",   es: "Media pensión incluida" },
  full_board: { en: "Full-board included",   fr: "Pension complète incluse", es: "Pensión completa incluida" },
};

// Parse the localized "capacity" string on a room ("2 guests", "2-4 huéspedes",
// "2-3 voyageurs"…) into a base occupancy number used to derive per-person
// prices when the CMS didn't supply one explicitly. Returns the LOWER bound
// of any range, because "from X€ per person" is quoted against the fullest
// price-sharing arrangement the room supports (i.e. two people in a double).
export const parseOccupancyBase = (capacityI18n) => {
  const raw =
    (capacityI18n && typeof capacityI18n === "object"
      ? capacityI18n.en || capacityI18n.fr || capacityI18n.es
      : capacityI18n) || "";
  const match = String(raw).match(/(\d+)/);
  if (!match) return 2;
  const n = parseInt(match[1], 10);
  return Number.isFinite(n) && n > 0 ? n : 2;
};

// Localized "Occupancy: N people" line shown on every price block.
export const pickOccupancyLabel = (count, lang = "en") => {
  const n = Math.max(1, parseInt(count, 10) || 2);
  const dict = {
    en: `Occupancy: ${n} ${n === 1 ? "person" : "people"}`,
    fr: `Occupation : ${n} ${n === 1 ? "personne" : "personnes"}`,
    es: `Ocupación: ${n} ${n === 1 ? "persona" : "personas"}`,
  };
  return dict[lang] || dict.en;
};

// Longer variant used inside price modals and reservation summaries.
export const pickOccupancyBasisLabel = (count, lang = "en") => {
  const n = Math.max(1, parseInt(count, 10) || 2);
  const dict = {
    en: `Based on an occupancy of ${n} ${n === 1 ? "person" : "people"}`,
    fr: `Basé sur une occupation de ${n} ${n === 1 ? "personne" : "personnes"}`,
    es: `Basado en una ocupación de ${n} ${n === 1 ? "persona" : "personas"}`,
  };
  return dict[lang] || dict.en;
};

// "per person and night" line — shown next to every amount.
export const pickPerPersonPerNightLabel = (lang = "en") => {
  const dict = {
    en: "per person and night",
    fr: "par personne et par nuit",
    es: "por persona y noche",
  };
  return dict[lang] || dict.en;
};

// "From" prefix used on price blocks (kept short for card layouts).
export const pickFromLabel = (lang = "en") => {
  const dict = { en: "From", fr: "À partir de", es: "Desde" };
  return dict[lang] || dict.en;
};
